import classnames from 'classnames'
import styles from './HomeProductPanningContainer.module.scss'
import { HOME_PRODUCT_POSITIONS } from './HomeProductPanningContainer.helper'
import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'
import useStore from '@/store'
import useWindowResize from '@/hooks/use-window-resize'
import { deviceInfo, getProductLinkObject, lerp, wait } from '@/utils'
import SanityImage from '@/components/SanityImage/SanityImage'
import { TRANSITION_MAP } from '@/sections/HomeProductList/HomeProductListTransitionBoxes/HomeProductListTransitionBoxes'
import { Flip } from 'gsap/dist/Flip'
import Link from '@/components/Link/Link'
import useBreakpoint from '@/hooks/use-breakpoint'
import { Draggable } from 'gsap/dist/Draggable'

gsap.registerPlugin(Draggable)
gsap.registerPlugin(Flip)

const COL_COUNT = 16
const ROW_COUNT = 8
const RENDERED_BOXES_COUNT = Object.keys(HOME_PRODUCT_POSITIONS).length
const LINES_COUNT = 160
const DEBUG = false

const SCALE_MOBILE = {
  ZOOM_IN: 2.7,
  ZOOM_DEFAULT: 1.8,
  // ZOOM_DEFAULT: 1,
  ZOOM_OUT: 1.3,
}

const SCALE_DESKTOP = {
  ZOOM_IN: 4.5,
  ZOOM_DEFAULT: 2.7,
  // ZOOM_DEFAULT: 1,
  ZOOM_OUT: 1.5,
}

const getGridTemplate = () => {
  return [...Array(ROW_COUNT)]
    .map((_, rowIndex) =>
      `
      "${[...Array(COL_COUNT)].map((_, colIndex) => `area-${rowIndex * COL_COUNT + colIndex}`).join(' ')}"
    `.trim(),
    )
    .join('\n')
}

const DEFAULT_FILTER = 'All'

const HomeProductPanningContainer = forwardRef(
  ({ className, children, products, transitionBoxesRef, onCalculationsSetCallback }, ref) => {
    const { isMobile } = useBreakpoint()
    const scaleValues = isMobile ? SCALE_MOBILE : SCALE_DESKTOP
    const [positionsSet, setPositionsSet] = useState(false)
    const setCursorPosition = useStore(state => state.setCursorPosition)
    const [activeFilter, setActiveFilter] = useState(DEFAULT_FILTER)
    const [filters, setFilters] = useState([])
    const [activeProductIndexHover, setActiveProductIndexHover] = useState(null)
    const loopedProducts = useMemo(() => {
      const maxLength = Object.keys(HOME_PRODUCT_POSITIONS).length
      const productList = []
      const filters = []
      for (let i = 0; i < maxLength; i++) {
        const productIndex = i % products.length
        const product = products[productIndex]
        if (product) {
          productList.push(product)
          if (product.lifestyleCategory.title && !filters.includes(product.lifestyleCategory.title)) {
            filters.push(product.lifestyleCategory.title)
          }
        }
      }
      setFilters([DEFAULT_FILTER, ...filters])
      return productList
    }, [products])
    const $items = useRef({})
    const $grid = useRef()
    const [scale, setScale] = useState(scaleValues.ZOOM_DEFAULT)
    const prevScale = useRef(null)
    const unsubRef = useRef()
    const $scaleContainer = useRef()
    const $scaleContainerInner = useRef()
    const $linesContainer = useRef()
    const $linesContainerInner = useRef()
    const resizeKey = useWindowResize()
    const [allowScale, setAllowScale] = useState(false)
    const debounceTimeout = useRef()
    const initialGridRatio = useRef(null)
    const [gridScaleMode, setGridScaleMode] = useState(null) // null, 'wider', 'taller'
    const scaleOffsetDistances = useRef({
      [scaleValues.ZOOM_IN]: { x: 0, y: 0 },
      [scaleValues.ZOOM_DEFAULT]: { x: 0, y: 0 },
      [scaleValues.ZOOM_OUT]: { x: 0, y: 0 },
    })
    const currentScaleOffsetDistancesLerp = useRef({ x: 0, y: 0 })
    const scaleOffsetMasterMultiplier = useRef(0)
    const [enableRaf, setEnableRaf] = useState(false)
    const allowMousePan = useRef(true)
    const [doneAnimatingIn, setDoneAnimatingIn] = useState(false)
    const isDoneAnimatingRef = useRef(false)
    const fromStates = useRef({})
    const $gridItemImages = useRef([])
    const elementScaleMultiplier = useRef(0)
    const elementsScaleValues = useRef([])
    const dragDistanceActiveTarget = useRef({ x: 0, y: 0 })
    const dragDistanceTarget = useRef({ x: 0, y: 0 })
    const draggableRef = useRef(null)
    // const setDebugMobileText = useStore(state => state.setDebugMobileText)
    const $gridInner = useRef(null)
    const [initialCalculationsSet, setInitialCalculationsSet] = useState(false)
    const [recalculateKey, setRecalculateKey] = useState(0)
    const $container = useRef(null)
    const panImmediate = useRef(false)
    const zoomPercentText = useRef(0)
    const $percentText = useRef(null)

    const getLerpTime = () => {
      if (deviceInfo?.browser?._parsedBrowserName?.toLowerCase() === 'safari') {
        return 4.5
      }
      return 4.5
    }

    useEffect(() => {
      if (!initialCalculationsSet) return
      if (onCalculationsSetCallback) onCalculationsSetCallback()
    }, [initialCalculationsSet, onCalculationsSetCallback])

    const resetDrag = () => {
      dragDistanceActiveTarget.current = { x: 0, y: 0 }
      dragDistanceTarget.current = { x: 0, y: 0 }
    }

    useEffect(() => {
      isDoneAnimatingRef.current = doneAnimatingIn
    }, [doneAnimatingIn])

    const initialCalculations = useCallback(async () => {
      if (!scaleValues) return

      await wait(15)

      if (!$grid.current) return

      const getElDimensions = () => {
        const value = {
          width: $grid.current.scrollWidth,
          height: $grid.current.scrollHeight,
        }

        return value
      }

      const windowRatio = window.innerWidth / window.innerHeight
      const elRect = getElDimensions()
      const elRatio = elRect.width / elRect.height

      // Fix for mobile - sometimes initial rendering is absolutely fucked
      // And it will not render the correct aspect ratio
      if (elRatio < 0.8) {
        setRecalculateKey(new Date().getTime())
        return
      }

      if (!initialGridRatio.current) {
        initialGridRatio.current = elRatio
        gsap.set($grid.current, {
          '--ratio': initialGridRatio.current,
        })
      }

      if (windowRatio > initialGridRatio.current) {
        // Window is wider than grid
        setGridScaleMode('wider')

        const _width = window.innerWidth
        gsap.set($grid.current, {
          '--width': `${_width}px`,
          '--height': `${_width / initialGridRatio.current}px`,
        })
      } else {
        // Window is taller than grid
        setGridScaleMode('taller')
        const _height = window.innerHeight
        gsap.set($grid.current, {
          '--width': `${_height * initialGridRatio.current}px`,
          '--height': `${_height}px`,
        })
      }

      await wait(5)

      scaleOffsetDistances.current[scaleValues.ZOOM_DEFAULT] = {
        x: (window.innerWidth - elRect.width * scaleValues.ZOOM_DEFAULT) * 0.5,
        y: (window.innerHeight - elRect.height * scaleValues.ZOOM_DEFAULT) * 0.5,
      }

      scaleOffsetDistances.current[scaleValues.ZOOM_OUT] = {
        x: (window.innerWidth - elRect.width * scaleValues.ZOOM_OUT) * 0.5,
        y: (window.innerHeight - elRect.height * scaleValues.ZOOM_OUT) * 0.5,
      }

      scaleOffsetDistances.current[scaleValues.ZOOM_IN] = {
        x: (window.innerWidth - elRect.width * scaleValues.ZOOM_IN) * 0.5,
        y: (window.innerHeight - elRect.height * scaleValues.ZOOM_IN) * 0.5,
      }

      setInitialCalculationsSet(true)

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scaleValues, recalculateKey])

    const scaleGrid = useCallback(
      (scale, immediate) => {
        if (prevScale.current === scale) return
        prevScale.current = scale
        if (!$scaleContainerInner.current) return
        gsap.killTweensOf([$scaleContainerInner.current, $linesContainerInner.current])

        const scaleContainerDuration = 1.2

        gsap[immediate ? 'set' : 'to']($scaleContainerInner.current, {
          scale: scale,
          duration: scaleContainerDuration,
          ease: 'Expo.easeOut',
        })
        gsap[immediate ? 'set' : 'to']($linesContainerInner.current, {
          scale: scale,
          duration: 2.2,
          ease: 'Expo.easeOut',
        })

        let setTo = 50
        if (scale === scaleValues.ZOOM_IN) {
          setTo = 100
        } else if (scale === scaleValues.ZOOM_OUT) {
          setTo = 25
        }

        gsap.killTweensOf(zoomPercentText.current)
        gsap.to(zoomPercentText, {
          current: setTo,
          duration: scaleContainerDuration * 0.7,
          ease: 'Power3.easeOut',
          onUpdate: () => {
            const value = Math.round(zoomPercentText.current)
            $percentText.current.innerHTML = value
          },
        })
      },
      [scaleValues],
    )

    const scaleItemsBasedOnDistance = (mouseX, mouseY) => {
      const elements = $gridItemImages.current
      const maxDistance = window.innerWidth * 0.5 // Maximum distance in pixels

      if (!elements || !mouseX || !mouseY) return []

      elements.forEach((element, i) => {
        if (!element) return 0

        const rect = element.getBoundingClientRect()
        const elementCenterX = rect.left + rect.width / 2
        const elementCenterY = rect.top + rect.height / 2

        // Get distance between mouse and element center using Pythagorean theorem
        const distance = Math.sqrt(Math.pow(mouseX - elementCenterX, 2) + Math.pow(mouseY - elementCenterY, 2))

        // Normalize distance between 0 and 1
        // Closer = higher value, farther = lower value
        const normalizedDistance = Math.max(0, Math.min(1, 1 - distance / maxDistance))
        const scale = 1 + normalizedDistance * 0.27 * elementScaleMultiplier.current

        // set default values
        if (!elementsScaleValues.current?.[i]) {
          elementsScaleValues.current[i] = {
            target: 1,
            current: 1,
          }
        }

        // Scale
        elementsScaleValues.current[i].target = scale + 0.0001

        elementsScaleValues.current[i].current = lerp(
          elementsScaleValues.current[i].current,
          elementsScaleValues.current[i].target,
          0.4,
        )

        gsap.set(element, {
          scale: elementsScaleValues.current[i].current,
        })
      })
    }

    const panContainer = mouseCoords => {
      if (!$scaleContainer.current) return
      if (!scaleOffsetDistances.current[prevScale.current]) return
      if (allowMousePan.current === false) return

      const yFromCenterMax = window.innerHeight * 0.5
      const yFromCenter = (mouseCoords.y - yFromCenterMax) / yFromCenterMax
      const xFromCenterMax = window.innerWidth * 0.5
      const xFromCenter = (mouseCoords.x - xFromCenterMax) / xFromCenterMax

      const y = yFromCenter * currentScaleOffsetDistancesLerp.current.y * scaleOffsetMasterMultiplier.current
      const x = xFromCenter * currentScaleOffsetDistancesLerp.current.x * scaleOffsetMasterMultiplier.current

      if (panImmediate.current) {
        gsap.set($scaleContainer.current, {
          x,
          y,
        })
      } else {
        const xTo = gsap.quickTo($scaleContainer.current, 'x', { duration: getLerpTime(), ease: 'power3' })
        const yTo = gsap.quickTo($scaleContainer.current, 'y', { duration: getLerpTime(), ease: 'power3' })
        xTo(x)
        yTo(y)
      }
    }

    const panContainerMobile = () => {
      if (!$scaleContainer.current) return

      const x = -(dragDistanceTarget.current.x + dragDistanceActiveTarget.current.x)
      const y = -(dragDistanceTarget.current.y + dragDistanceActiveTarget.current.y)

      const xTo = gsap.quickTo($scaleContainer.current, 'x', { duration: 0.7, ease: 'power3' })
      const yTo = gsap.quickTo($scaleContainer.current, 'y', { duration: 0.7, ease: 'power3' })

      xTo(x)
      yTo(y)
    }

    useEffect(() => {
      if (isMobile === null || !initialCalculationsSet) return
      const items = []

      getAllBoxes().forEach($el => {
        if (!$el) return
        const $img = $el.querySelector('img')
        if ($img) {
          items.push($img)
        }
      })

      $gridItemImages.current = items

      function raf() {
        if (isMobile) {
          panContainerMobile()
        } else {
          const mouseCoords = useStore.getState().cursorPosition
          scaleItemsBasedOnDistance(mouseCoords.x, mouseCoords.y)
        }
      }

      gsap.ticker.remove(raf)
      gsap.ticker.add(raf)

      return () => {
        gsap.ticker.remove(raf)
      }
    }, [enableRaf, isMobile, initialCalculationsSet])

    useEffect(() => {
      initialCalculations()
      setAllowScale(false)

      if (!initialCalculationsSet) return

      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
      debounceTimeout.current = setTimeout(() => {
        prevScale.current = null
        setScale(scaleValues.ZOOM_DEFAULT)
        scaleGrid(scaleValues.ZOOM_DEFAULT, true)
        setAllowScale(true)
      }, 50)
    }, [resizeKey, scaleValues, isMobile, initialCalculations, initialCalculationsSet, scaleGrid])

    useEffect(() => {
      if (!allowScale || !scaleOffsetDistances?.current?.[scale] || !initialCalculationsSet || isMobile === null) return
      scaleGrid(scale)

      gsap.killTweensOf(currentScaleOffsetDistancesLerp.current)
      gsap.to(currentScaleOffsetDistancesLerp.current, {
        x: scaleOffsetDistances.current[scale].x,
        y: scaleOffsetDistances.current[scale].y,
        duration: 0.6,
        ease: 'Expo.easeOut',
      })
    }, [scale, allowScale, initialCalculationsSet, isMobile, scaleGrid])

    useEffect(() => {
      if (!initialCalculationsSet) return

      if (unsubRef.current) unsubRef.current()

      if (isMobile) return

      unsubRef.current = useStore.subscribe(
        state => [state.cursorPosition],
        value => {
          const mouseCoords = value[0]
          panContainer(mouseCoords)
        },
      )

      return () => {
        if (unsubRef.current) unsubRef.current()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, initialCalculationsSet])

    const getAnimatedBoxes = () => {
      const boxes = {}
      const $els = document.querySelectorAll('[data-image-asset]:not([data-image-asset="null"])')
      $els.forEach(($el, i) => {
        const tiedToIndex = $el.dataset.boxIndex
        const $elTiedTo = document.querySelectorAll(`[data-tied-to-index="${tiedToIndex}"]`)[0]
        const isTop = $elTiedTo.dataset.isTop === 'true'

        if (!$elTiedTo || !$el || !tiedToIndex) return

        boxes[i] = {
          $el,
          $elTiedTo,
          isTop,
          tiedToIndex,
        }
      })
      return boxes
    }

    const getAllBoxes = () => {
      const $els = document.querySelectorAll('[data-image-asset]')
      return $els
    }

    const setBoxInitialPositions = () => {
      if (!$grid.current) return
      Object.values(getAnimatedBoxes()).forEach(obj => {
        const $el = obj?.$el
        const $elTiedTo = obj?.$elTiedTo
        if (!$el || !$elTiedTo || !obj?.tiedToIndex) return

        gsap.killTweensOf($el)

        const isTop = obj?.isTop

        if (isTop) {
          $el.dataset.isTop = 'true'
        }

        const fromState = Flip.getState($el)

        fromStates.current[obj?.tiedToIndex] = {
          fromState,
          $el,
        }

        Flip.fit($el, $elTiedTo, {
          scale: true,
        })

        gsap.set($el, {
          zIndex: isTop ? 10 : 1,
          opacity: 1,
        })
      })

      setPositionsSet(true)
    }

    const animateBoxesIntoPlace = (callback, onlyAnimateTop = false) => {
      if (!$grid.current) return

      const onComplete = () => {
        setDoneAnimatingIn(true)

        if (!transitionBoxesRef.current) return

        transitionBoxesRef.current.setRender(false)

        setEnableRaf(true)

        gsap.to(elementScaleMultiplier, {
          current: 1,
          duration: 1.2,
          ease: 'Expo.easeOut',
        })

        if (callback) callback()
      }

      if (!onlyAnimateTop) {
        transitionBoxesRef.current.visuallyHide()

        getAllBoxes().forEach($el => {
          if (!$el) return
          if ($el.dataset.imageAsset === 'null') {
            gsap.to($el, {
              opacity: 1,
              delay: 1,
              duration: 1.2,
            })
          }
        })
      }

      if (onlyAnimateTop) {
        getAllBoxes().forEach($el => {
          if (!$el) return
          const $img = $el.querySelector('img')
          if ($img) {
            gsap.to($img, {
              opacity: 1,
              duration: 0.8,
            })
          }
        })
      }

      let duration = 1.6
      let delay = 0.15

      const isSafariDesktop = deviceInfo?.isSafariDesktop
      if (isSafariDesktop) {
        duration = 0.1
        delay = 0.1

        // just fire on complete because she dont work otherwise
        setTimeout(
          () => {
            onComplete()
          },
          duration * 1000 + delay * 1000 + 10,
        )
      }

      const items = Object.values(getAnimatedBoxes())

      items.forEach((obj, i) => {
        const { tiedToIndex } = obj
        const fromState = fromStates?.current?.[tiedToIndex]?.fromState
        // const $elToAnimateTo = fromStates?.current?.[tiedToIndex]?.$el
        if (!fromState) return

        // const isTop = Object.values(getAnimatedBoxes()).map(obj => obj.isTop && obj.tiedToIndex === tiedToIndex)[0].isTop
        let isTop = false
        Object.values(getAnimatedBoxes()).forEach(obj => {
          if (obj.isTop && obj.tiedToIndex === tiedToIndex) {
            isTop = true
          }
        })

        if (onlyAnimateTop && !isTop) return
        if (!onlyAnimateTop && isTop) return

        Flip.to(fromState, {
          duration: onlyAnimateTop ? duration * 0.8 : duration,
          ease: 'Expo.easeInOut',
          scale: true,
          delay: onlyAnimateTop ? 0 : i * delay,
          onStart: () => {
            if (onlyAnimateTop && transitionBoxesRef.current) {
              transitionBoxesRef.current.disallowHover()
            }
          },
          onComplete: () => {
            if (onlyAnimateTop) {
              onComplete()

              // if (isSafariDesktop) {
              //   setActiveProductIndexHover(1)
              //   setTimeout(() => {
              //     setActiveProductIndexHover(null)
              //   }, 50)
              // }
            }
          },
        })
      })
    }

    useImperativeHandle(ref, () => ({
      scaleOffsetMasterMultiplier,
      animateBoxesIntoPlace,
      setBoxInitialPositions,
    }))

    const buttonsOnMouseEnter = () => {
      setCursorPosition({ x: window.innerWidth * 0.5, y: window.innerHeight * 0.5 })
      allowMousePan.current = false

      gsap.killTweensOf(scaleOffsetMasterMultiplier)
      gsap.to(scaleOffsetMasterMultiplier, {
        current: 0,
        duration: 0.2,
      })
    }

    const buttonsOnMouseLeave = () => {
      allowMousePan.current = true

      gsap.killTweensOf(scaleOffsetMasterMultiplier)
      gsap.to(scaleOffsetMasterMultiplier, {
        current: 1,
        duration: 1.5,
      })
    }

    useEffect(() => {
      if (!doneAnimatingIn || !initialCalculationsSet) return
      const items = document.querySelectorAll('[data-lifestyle-category]')
      if (!items.length) return
      items.forEach(item => {
        if (!item) return
        gsap.killTweensOf(item)
        if (activeFilter === DEFAULT_FILTER || item.dataset.lifestyleCategory === activeFilter) {
          gsap.to(item, {
            opacity: 1,
            duration: 0.6,
          })
        } else {
          gsap.to(item, {
            opacity: 0.2,
            duration: 0.6,
          })
        }
      })
    }, [activeFilter, doneAnimatingIn, initialCalculationsSet])

    useEffect(() => {
      if (isMobile) return
      if (!positionsSet) return
      if (Object.keys($items.current).length === 0) return

      const isSafariDesktop = deviceInfo?.isSafariDesktop

      Object.keys($items.current).forEach(key => {
        const $el = $items?.current?.[key]
        if (!$el) return
        if (isDoneAnimatingRef.current === false && $el.dataset.imageAsset !== 'null') {
          return
        }

        gsap.killTweensOf($el, 'scale')
        gsap.set($el, {
          zIndex: activeProductIndexHover === parseInt(key) ? 2 : 1,
        })

        const scale = isSafariDesktop ? 1.0002 : 1.5
        const duration = isSafariDesktop ? 0.0001 : 0.6

        gsap.to($el, {
          scale: activeProductIndexHover === parseInt(key) ? scale : 1.0001,
          duration: duration,
          ease: 'Power4.easeOut',
        })
      })
    }, [activeProductIndexHover, positionsSet, isMobile])

    useEffect(() => {
      if (!isMobile || !$scaleContainerInner.current || !allowScale) return

      const calculateBounds = () => {
        if (!$scaleContainerInner.current || !$grid.current) return { x: 0, y: 0 }

        const containerWidth = $scaleContainerInner.current.offsetWidth
        const containerHeight = $scaleContainerInner.current.offsetHeight
        const gridWidth = $grid.current.scrollWidth
        const gridHeight = $grid.current.scrollHeight

        // Calculate max bounds based on current scale
        const maxX = Math.round(Math.max(0, (gridWidth * scale - containerWidth) / 2))
        const maxY = Math.round(Math.max(0, (gridHeight * scale - containerHeight) / 2))

        return { maxX, maxY }
      }

      draggableRef.current = Draggable.create($gridInner.current, {
        type: 'x,y',
        edgeResistance: 0.2,
        liveSnap: false,
        inertia: false,
        autoScroll: false,
        onDrag() {
          const bounds = calculateBounds()
          if (!bounds) return

          // Calculate new position with bounds
          const newX = Math.max(-bounds.maxX, Math.min(bounds.maxX, (this.endX - this.startX) * -1))
          const newY = Math.max(-bounds.maxY, Math.min(bounds.maxY, (this.endY - this.startY) * -1))

          let multiplier = 3
          if (scaleValues.ZOOM_IN === scale) {
            multiplier = 4
          } else if (scaleValues.ZOOM_OUT === scale) {
            multiplier = 2
          }

          dragDistanceActiveTarget.current = {
            x: newX * multiplier,
            y: newY * multiplier,
          }
        },
        onDragEnd() {
          const bounds = calculateBounds()
          if (!bounds) return

          // Calculate final position with bounds
          const newX = Math.max(
            -bounds.maxX,
            Math.min(bounds.maxX, dragDistanceTarget.current.x + dragDistanceActiveTarget.current.x),
          )
          const newY = Math.max(
            -bounds.maxY,
            Math.min(bounds.maxY, dragDistanceTarget.current.y + dragDistanceActiveTarget.current.y),
          )

          dragDistanceTarget.current = {
            x: newX,
            y: newY,
          }
          dragDistanceActiveTarget.current = { x: 0, y: 0 }
        },
      })
    }, [isMobile, scale, resizeKey, allowScale, scaleValues])

    return (
      <div
        className={classnames(
          styles.HomeProductPanningContainer,
          className,
          { [styles.isDebug]: DEBUG },
          { [styles.isDoneAnimatingIn]: doneAnimatingIn },
        )}
        data-window-scale={gridScaleMode}
        style={{
          '--col-count': COL_COUNT,
          '--row-count': ROW_COUNT,
          '--grid-template': getGridTemplate(),
        }}
        ref={$container}
      >
        <div
          className={styles.linesContainer}
          ref={$linesContainer}
        >
          <div
            className={styles.linesContainerInner}
            ref={$linesContainerInner}
          >
            {Array(LINES_COUNT)
              .fill(null)
              .map((_, i) => {
                return (
                  <div
                    className={styles.lineHorizontal}
                    data-line-horizontal={i}
                    key={i}
                  />
                )
              })}
            {Array(LINES_COUNT)
              .fill(null)
              .map((_, i) => {
                return (
                  <div
                    className={styles.lineVertical}
                    key={i}
                    data-line-vertical={i}
                  />
                )
              })}
          </div>
        </div>
        <div
          className={styles.scaleContainer}
          ref={$scaleContainer}
        >
          <div
            className={styles.scaleContainerInner}
            ref={$scaleContainerInner}
          >
            {children}
            <div
              className={styles.grid}
              ref={$grid}
            >
              <div
                className={styles.gridInner}
                ref={$gridInner}
              >
                {[...Array(RENDERED_BOXES_COUNT)].map((_, i) => {
                  const product = loopedProducts[i]

                  return (
                    <div
                      className={styles.gridItem}
                      key={i}
                      style={{
                        '--item-color': `var(--${HOME_PRODUCT_POSITIONS[i]?.color})`,
                        '--item-position': `area-${HOME_PRODUCT_POSITIONS[i]?.pos}`,
                      }}
                      data-box-index={i}
                      data-count={i}
                      data-lifestyle-category={product?.lifestyleCategory?.title}
                      data-image-asset={
                        Object.values(TRANSITION_MAP).includes(i) ? JSON.stringify(product?.image) : 'null'
                      }
                      onMouseEnter={() => {
                        if (!product || isMobile) return
                        setActiveProductIndexHover(i)
                      }}
                      onMouseLeave={() => {
                        if (!product || isMobile) return
                        setActiveProductIndexHover(null)
                      }}
                      ref={ref => {
                        if (!ref) return
                        if (!product) return
                        $items.current[i] = ref
                      }}
                    >
                      {product && (
                        <Link
                          link={getProductLinkObject(product.slug.current, product.title)}
                          className={styles.gridItem__imageContainer}
                          ariaLabel={`Learn more about ${product.title}`}
                        >
                          <SanityImage
                            image={product.image}
                            className={styles.gridItem__image}
                            disableLoadedOpacity
                            priority
                            breakpoints={{
                              xs: {
                                width: 400,
                              },
                              tablet: {
                                width: deviceInfo?.isSafariDesktop ? 650 : 500,
                              },
                            }}
                          />
                        </Link>
                      )}
                    </div>
                  )
                })}
                {DEBUG && (
                  <>
                    {[...Array(COL_COUNT * ROW_COUNT)].map((_, i) => {
                      return (
                        <div
                          className={styles.debugGridItem}
                          key={i}
                          style={{
                            '--item-position': `area-${i}`,
                          }}
                          data-count={i}
                        >
                          <span>{i}</span>
                        </div>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <ul className={styles.filters}>
          {filters.map((filter, i) => {
            return (
              <Fragment key={i}>
                {i !== 0 && (
                  <li
                    aria-hidden
                    tabIndex="-1"
                    className={styles.filter__separator}
                  >
                    /
                  </li>
                )}
                <li className={classnames(styles.filter, { [styles.isActive]: activeFilter === filter })}>
                  <button
                    className={styles.filter__button}
                    aria-label={`filter products to category ${filter}`}
                    onClick={() => {
                      if (filter === activeFilter) {
                        setActiveFilter(DEFAULT_FILTER)
                      } else {
                        setActiveFilter(filter)
                      }
                    }}
                  >
                    {filter}
                  </button>
                </li>
              </Fragment>
            )
          })}
        </ul>
        <div
          className={styles.buttonsContainer}
          onMouseEnter={buttonsOnMouseEnter}
          onMouseLeave={buttonsOnMouseLeave}
        >
          [
          <button
            className={classnames(styles.button, styles.zoomIn)}
            aria-label="Zoom in"
            onClick={() => {
              resetDrag()

              setScale(prev => {
                if (prev === scaleValues.ZOOM_OUT) return scaleValues.ZOOM_DEFAULT
                return scaleValues.ZOOM_IN
              })
              // setEnableRaf(true)
              // setTimeout(() => {
              //   setEnableRaf(false)
              // }, getLerpTime() * 1000)
            }}
          >
            <span>+</span>
          </button>
          |
          <button
            className={classnames(styles.button, styles.zoomOut)}
            aria-label="Zoom out"
            onClick={() => {
              resetDrag()

              setScale(prev => {
                if (prev === scaleValues.ZOOM_IN) return scaleValues.ZOOM_DEFAULT
                return scaleValues.ZOOM_OUT
              })
              // setEnableRaf(true)
              // setTimeout(() => {
              //   setEnableRaf(false)
              // }, getLerpTime() * 1000)
            }}
          >
            <span>-</span>
          </button>
          ]
        </div>
        <div className={styles.percentContainer}>
          <span
            className={styles.percentText}
            ref={$percentText}
          >
            00
          </span>
          <span className={styles.percentText}>%</span>
        </div>
        {/* {process.env.NODE_ENV === 'development' && (
        <div
          className={styles.debugText}
          ref={$debugText}
        />
      )} */}
      </div>
    )
  },
)

HomeProductPanningContainer.displayName = 'HomeProductPanningContainer'

export default HomeProductPanningContainer
